import React from 'react';
import { Loader2 } from 'lucide-react';

export interface ButtonProps {
  /** Button visual style */
  variant?: 'primary' | 'accent' | 'tonal' | 'ghost' | 'danger' | 'success' | 'icon';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Show loading spinner */
  loading?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Icon element (Lucide icon or ReactNode) */
  icon?: React.ReactNode;
  /** Icon position relative to text */
  iconPosition?: 'left' | 'right';
  /** Full width button */
  fullWidth?: boolean;
  /** HTML button type */
  type?: 'button' | 'submit' | 'reset';
  /** Wenn gesetzt, wird ein `<a>` mit gleichem Styling gerendert (z. B. mailto:, tel:) */
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  /** Click handler */
  onClick?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  /** Additional CSS classes */
  className?: string;
  /** Native title (Tooltip) */
  title?: string;
  /** Barrierefreie Beschriftung (z. B. Icon-only) */
  'aria-label'?: string;
  /** Button content */
  children?: React.ReactNode;
}

/**
 * Canonical Button component – replaces all 15+ inline button styles.
 *
 * Variants:
 * - `primary`: White CTA on dark (main action)
 * - `accent`: Teal background (accent action)
 * - `tonal`: Soft accent background with accent text
 * - `ghost`: Transparent with border (secondary action)
 * - `danger`: Soft red background with red text
 * - `success`: Grüner CTA (z. B. tel:-Links)
 * - `icon`: Square icon-only button with border
 *
 * All variants use pill shape, spring animations, and token-based colors.
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  type = 'button',
  href,
  target,
  rel,
  onClick,
  className = '',
  title,
  'aria-label': ariaLabel,
  children,
}) => {
  // Base styles – always applied
  const baseStyles = [
    'inline-flex items-center justify-center',
    'font-body font-semibold',
    'cursor-pointer border-none no-underline',
    'transition-all duration-200 ease-spring-bounce',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none',
    'active:scale-[0.94]',
  ].join(' ');

  // Variant styles
  const variantStyles: Record<string, string> = {
    primary: [
      'bg-primary text-primary-contrast',
      'rounded-pill',
      'shadow-glow-white',
      'hover:shadow-lg hover:-translate-y-0.5',
    ].join(' '),

    accent: [
      'bg-accent text-txt-inverted',
      'rounded-pill',
      'hover:shadow-glow-accent hover:-translate-y-0.5',
    ].join(' '),

    tonal: [
      'bg-accent-medium text-accent',
      'rounded-pill',
      'hover:bg-accent-glow',
    ].join(' '),

    ghost: [
      'bg-transparent text-txt-secondary',
      'rounded-pill',
      'border border-default',
      'hover:border-strong hover:text-txt hover:bg-bg-card',
    ].join(' '),

    danger: [
      'bg-danger-medium text-danger',
      'rounded-pill',
      'hover:bg-danger-medium',
    ].join(' '),

    success: [
      'bg-success text-txt-inverted',
      'rounded-pill',
      'hover:brightness-110 hover:-translate-y-0.5',
    ].join(' '),

    icon: [
      'bg-transparent text-txt-tertiary',
      'rounded-pill',
      'border border-default',
      'hover:border-strong hover:text-txt',
      '!p-0', // Override padding for square shape
    ].join(' '),
  };

  // Size styles
  const sizeStyles: Record<string, string> = {
    sm: 'px-3.5 py-1.5 text-xs gap-[7px]',
    md: 'px-[22px] py-2.5 text-[13px] gap-[7px]',
    lg: 'px-7 py-3 text-[14.5px] gap-2',
  };

  // Icon button sizes (square)
  const iconSizeStyles: Record<string, string> = {
    sm: 'w-7 h-7',
    md: 'w-[34px] h-[34px]',
    lg: 'w-[42px] h-[42px]',
  };

  // Icon element sizes
  const iconElSizes: Record<string, string> = {
    sm: 'w-3.5 h-3.5',
    md: 'w-[14px] h-[14px]',
    lg: 'w-5 h-5',
  };

  // Build class string
  const isIconOnly = variant === 'icon';
  const buttonClasses = [
    baseStyles,
    variantStyles[variant] || variantStyles.primary,
    isIconOnly ? iconSizeStyles[size] : sizeStyles[size],
    fullWidth ? 'w-full' : '',
    className,
  ].filter(Boolean).join(' ');

  // Render icon with correct size
  const renderIcon = (iconNode: React.ReactNode) => {
    if (!iconNode) return null;
    const sizeClass = iconElSizes[size];

    if (typeof iconNode === 'function') {
      const IconComponent = iconNode as React.ComponentType<{ className?: string }>;
      return <IconComponent className={sizeClass} />;
    }

    if (React.isValidElement(iconNode)) {
      return React.cloneElement(iconNode as React.ReactElement<any>, {
        className: sizeClass,
      });
    }

    return <span className={sizeClass}>{iconNode}</span>;
  };

  const content = (
    <>
      {loading ? (
        <Loader2 className={`${iconElSizes[size]} animate-spin`} />
      ) : (
        icon && (isIconOnly || iconPosition === 'left') && renderIcon(icon)
      )}

      {!isIconOnly && children && <span>{children}</span>}

      {!loading && !isIconOnly && icon && iconPosition === 'right' && renderIcon(icon)}
    </>
  );

  const isDisabled = disabled || loading;

  if (href && !isDisabled) {
    return (
      <a
        href={href}
        target={target}
        rel={rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined)}
        onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}
        className={buttonClasses}
        title={title}
        aria-label={ariaLabel}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
      disabled={isDisabled}
      className={buttonClasses}
      title={title}
      aria-label={ariaLabel}
    >
      {content}
    </button>
  );
};

export default Button;
